import { useEffect, useRef, useState } from 'react';
import type QrScanner from 'qr-scanner';
import styles from '@/pages/partner-jobs/PartnerJobs.module.css';

/**
 * Scans a charger's QR with the phone's camera (the same decoder as the driver app's scanner, loaded only here), or
 * takes the serial or ID typed from the label when the camera cannot read it. Calls onCode once per scan.
 */
export function QrScan({ onCode, onClose }: { onCode: (text: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null), scanner = useRef<QrScanner | null>(null), done = useRef(false);
  const [state, setState] = useState<'starting' | 'scanning' | 'unsupported' | 'denied'>('starting'), [typed, setTyped] = useState('');
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setState('unsupported'); return; }
      try {
        const { default: Ctor } = await import('qr-scanner');
        if (cancelled || !video.current) return;
        if (!(await Ctor.hasCamera())) { setState('unsupported'); return; }
        const s = new Ctor(video.current, r => { if (done.current) return; done.current = true; s.stop(); onCode(r.data); },
          { preferredCamera: 'environment', maxScansPerSecond: 5, highlightScanRegion: true, returnDetailedScanResult: true });
        scanner.current = s;
        await s.start();
        if (!cancelled) setState('scanning');
      } catch (err) {
        if (!cancelled) setState(err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'SecurityError') ? 'denied' : 'unsupported');
      }
    })();
    return () => { cancelled = true; scanner.current?.destroy(); scanner.current = null; };
  }, []);
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <video ref={video} muted playsInline style={{ width: '100%', borderRadius: 12, background: 'var(--evi-surface-2)', display: state === 'scanning' || state === 'starting' ? 'block' : 'none' }} />
    {state === 'denied' && <p className={styles.hint}>The camera is blocked for this site. Allow it in your browser settings, or type the serial below.</p>}
    {state === 'unsupported' && <p className={styles.hint}>This phone cannot scan here. Type the serial or charger ID from the label.</p>}
    <form style={{ display: 'flex', gap: 8 }} onSubmit={e => { e.preventDefault(); if (typed.trim()) onCode(typed.trim()); }}>
      <label className={styles.field} style={{ flex: 1 }}><span>Or type the serial or ID</span><input value={typed} onChange={e => setTyped(e.target.value)} maxLength={200} autoCapitalize="characters" /></label>
      <button type="submit" className={styles.ghost} style={{ alignSelf: 'flex-end' }} disabled={!typed.trim()}>Check</button>
    </form>
    <button type="button" className={styles.ghost} onClick={onClose}>Close</button>
  </div>;
}
